import { MouseEvent, SyntheticEvent, useEffect, useState } from "react"

import { ReactComponent as ArrowBack } from "@common_assets/svg/arrowBack.svg"
import { ReactComponent as IconChevronDown } from "@common_assets/svg/chevron-down.svg"
import { ReactComponent as IconExport } from "@common_assets/svg/export.svg"
import { ReactComponent as IconFilters } from "@common_assets/svg/filters.svg"
import { ReactComponent as ThreeDots } from "@common_assets/svg/threeDots.svg"
import { yupResolver } from "@hookform/resolvers/yup"
import {
  Box,
  Button,
  Divider,
  Fab,
  Grid,
  Stack,
  Tab,
  Tabs,
  Typography,
  useTheme,
  useMediaQuery,
  CircularProgress,
  styled,
} from "@mui/material"
import {
  useMutationEditStudentJobs,
  useMutationRemoveStudent,
  useMutationUpdateStudentClasses,
} from "api/reactQuery/mutations/students"
import {
  useQueryExportStudentJobsTask,
  useQueryExportStudentStoreTransactionsTask,
  useQueryExportStudentTransactionsTask,
} from "api/reactQuery/queries/export"
import { ExportType } from "api/reactQuery/queries/export.types"
import { useQueryStudentsClasses } from "api/reactQuery/queries/students"
import { IResponseStudentClasses } from "api/reactQuery/queries/students.types"
import { useQueryStudentRetrieve } from "api/reactQuery/queries/studentsSearch"
import Dialog from "components/common/dialog/dialog"
import DialogUpgradeAccount from "components/common/dialogUpgradeAccount/DialogUpgradeAccount"
import { ExportPendingBanner, InlineExportSubmenu } from "components/common/exportMenu"
import { PopRow, PopOverStyle } from "components/common/poprow"
import StudentSkeleton from "components/common/skeleton/studentProfile"
import FileField from "components/form/common/field/fileField"
import DialogRemoveStudent from "components/module/classStudentsTeacher/common/DialogRemoveStudent"
import Accounts from "components/module/studentProfile/accounts/Accounts"
import {
  Transactions,
  AccountInformation,
} from "components/module/studentProfile/index"
import { useDialog } from "hooks/dialog"
import { useExportGuard, useExportTasks } from "hooks/exportReports"
import { useCustomPayment } from "hooks/payment"
import { usePopover } from "hooks/popover"
import { useCanDeleteStudent } from "hooks/schoolPlans"
import { useCustomSnackbar } from "hooks/snackbar"
import { useAppSelector } from "hooks/store"
import mixpanel from "mixpanel-browser"
import { useForm, FormProvider } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { useQueryClient } from "react-query"
import { useNavigate, useLocation, useParams } from "react-router-dom"
import { RoutePath } from "routes/Route.types"
import { selectUser } from "store/auth/auth.selectors"
import { UserSchoolPlan } from "ts/enums/User"
import { getErrorMessage, getErrorMessageFromTab } from "utils/api"
import { isValidClassId } from "utils/classId"
import { hiddenExport, schoolPlanToExportData } from "utils/export"

import IconWrapper from "../../common/icon/iconWrapper"
import Goals from "./goals"
import { ManageStudentClasses, ManageStudentJobs } from "./manageStudent"
import { validateSchema } from "./studentProfile.config"
import { LocationState } from "./StudentProfile.types"
const TabStyle = styled(Tab)({
  paddingTop: "10px",
})

const PopoverBox = styled(Box)({
  display: "flex",
  columnGap: "8px",
  alignItems: "center",
})

const StudentProfile = () => {
  const location = useLocation()
  const state = location.state as LocationState
  const params = useParams()
  const userInfo = useAppSelector(selectUser)
  const { studentId } = params
  const queryClient = useQueryClient()
  const { showSnackbar } = useCustomSnackbar()

  const theme = useTheme()
  const mobile = useMediaQuery("(max-width:600px)")
  const { t } = useTranslation()
  const canDeleteStudent = useCanDeleteStudent()

  const TabsStyle = styled(Tabs)({
    "&.MuiTabs-root .Mui-disabled": {
      opacity: mobile ? "0.3" : "0",
    },
  })

  const [errorInfo, setErrorInfo] = useState<string | null>(null)
  const [hover, setHover] = useState(false)
  const [classes, setClasses] = useState<IResponseStudentClasses[]>([])
  const [checked, setChecked] = useState<number[]>([])
  const [checkedJobs, setCheckedJobs] = useState<number[]>([])
  const [selectedClass, setSelectedClass] = useState("")
  const navigate = useNavigate()
  const [val, setVal] = useState<string | null>(null)
  const [isExportDataTransactions, setIsExportDataTransactions] = useState(false)
  const [isExportDataJobs, setIsExportDataJobs] = useState(false)
  const [isExportDataStoreTransactions, setIsExportDataStoreTransactions] = useState(false)
  const [isExportMenuOpenTransactions, setIsExportMenuOpenTransactions] = useState(false)
  const [isExportMenuOpenJobs, setIsExportMenuOpenJobs] = useState(false)
  const [isExportMenuOpenStoreTransactions, setIsExportMenuOpenStoreTransactions] = useState(false)

  const methods = useForm({
    resolver: yupResolver(validateSchema),
  })

  const { isSuccess: isSuccessClasses } = useQueryStudentsClasses({
    studentId: Number(studentId),
    options: {
      enabled: !!studentId,
      cacheTime: 0,
      onSuccess: (data) => {
        setClasses(data?.data)
      },
    },
  })

  const stateClassId = state?.classId
  const classId =
    isValidClassId(stateClassId) &&
    (!isSuccessClasses ||
      classes.some((el) => el.id === stateClassId && el.is_in_class))
      ? stateClassId
      : undefined
  const exportClassId = isSuccessClasses ? classId : undefined

  const { isLoading, data, isSuccess } = useQueryStudentRetrieve({
    params: Number(studentId),
    options: {
      cacheTime: 0,
      onError: (error) => {
        setErrorInfo(t(getErrorMessage(error)))
      },
    },
  })

  const { payIt, isLoadingPayments } = useCustomPayment()

  const {
    successTasks: successTasksTransactions,
    pendingTasks: pendingTasksTransactions,
    setExportReportIdToDownload: setExportReportIdToDownloadTransactions,
    handleExportSuccess: handleExportSuccessTransactions,
  } = useExportTasks({
    exportType: ExportType.STUDENT_TRANSACTIONS,
    classId: Number(studentId),
  })

  const { isFetching: isFetchingExportTransactions, refetch: refetchExportDataTransactions } =
    useQueryExportStudentTransactionsTask({
      studentId: Number(studentId),
      options: {
        enabled: isExportDataTransactions,
        onSuccess: (response) => {
          setIsExportDataTransactions(false)
          handleExportSuccessTransactions(response, Number(studentId))
          showSnackbar({
            title: t("export.fileIsCurrentlyBeingGenerated"),
          })
        },
        onError: (err) => {
          showSnackbar({
            title: getErrorMessageFromTab(err),
            variant: "error",
          })
        },
      },
    })

  const {
    successTasks: successTasksJobs,
    pendingTasks: pendingTasksJobs,
    setExportReportIdToDownload: setExportReportIdToDownloadJobs,
    handleExportSuccess: handleExportSuccessJobs,
  } = useExportTasks({
    exportType: ExportType.STUDENT_JOBS,
    classId: Number(studentId),
  })

  const { isFetching: isFetchingExportJobs, refetch: refetchExportDataJobs } =
    useQueryExportStudentJobsTask({
      studentId: Number(studentId),
      options: {
        enabled: isExportDataJobs,
        onSuccess: (response) => {
          setIsExportDataJobs(false)
          handleExportSuccessJobs(response, Number(studentId))
          showSnackbar({
            title: t("export.fileIsCurrentlyBeingGenerated"),
          })
        },
        onError: (err) => {
          showSnackbar({
            title: getErrorMessageFromTab(err),
            variant: "error",
          })
        },
      },
    })

  const {
    successTasks: successTasksStoreTransactions,
    pendingTasks: pendingTasksStoreTransactions,
    setExportReportIdToDownload: setExportReportIdToDownloadStoreTransactions,
    handleExportSuccess: handleExportSuccessStoreTransactions,
  } = useExportTasks({
    exportType: ExportType.STUDENT_STORE_TRANSACTIONS,
    classId: Number(studentId),
  })

  const { isFetching: isFetchingExportStoreTransactions, refetch: refetchExportDataStoreTransactions } =
    useQueryExportStudentStoreTransactionsTask({
      classId: exportClassId ?? 0,
      studentId: Number(studentId),
      options: {
        enabled: isExportDataStoreTransactions && !!exportClassId,
        onSuccess: (response) => {
          setIsExportDataStoreTransactions(false)
          handleExportSuccessStoreTransactions(response, Number(studentId))
          showSnackbar({
            title: t("export.fileIsCurrentlyBeingGenerated"),
          })
        },
        onError: (err) => {
          showSnackbar({
            title: getErrorMessageFromTab(err),
            variant: "error",
          })
        },
      },
    })

  const { isGenerateDisabled: isDisabledTransactions } = useExportGuard({
    hasPendingTasks: pendingTasksTransactions.length > 0,
  })
  const { isGenerateDisabled: isDisabledJobs } = useExportGuard({
    hasPendingTasks: pendingTasksJobs.length > 0,
  })
  const { isGenerateDisabled: isDisabledStoreTransactions } = useExportGuard({
    hasPendingTasks: pendingTasksStoreTransactions.length > 0,
  })

  const handleToggleExportMenuTransactions = () => {
    setIsExportMenuOpenTransactions((prev) => !prev)
  }

  const handleToggleExportMenuJobs = () => {
    setIsExportMenuOpenJobs((prev) => !prev)
  }

  const handleToggleExportMenuStoreTransactions = () => {
    setIsExportMenuOpenStoreTransactions((prev) => !prev)
  }

  useEffect(() => {
    methods.setValue("avatar_url", data?.data.avatar_url)
    setVal(methods.getValues("avatar_url"))
  }, [isSuccess])

  const { mutate, isLoading: isLoadingUpdateStudentClass } =
    useMutationUpdateStudentClasses({
      options: {
        onSuccess: (_, variable) => {
          if (variable.classes.length === 0) {
            showSnackbar({
              title: t("studentProfile.studentIsntAssignedToAnyClass", {
                firstName: data?.data.first_name,
                lastName: data?.data.last_name,
              }),
              variant: "info",
            })
          }
          queryClient.invalidateQueries("students")
          queryClient.invalidateQueries("classesStudents")
          showSnackbar({
            title: t("studentProfile.updateSucceeded"),
            variant: "success",
          })
          setChecked([])
          handleCloseDialogManageStudentClasses()
        },
        onError: (error) => {
          showSnackbar({
            title: getErrorMessage(error),
            variant: "error",
          })
        },
      },
    })

  const { mutate: editStudentJobs } = useMutationEditStudentJobs({
    options: {
      onSuccess: () => {
        queryClient.invalidateQueries("possibleJobs")
        setCheckedJobs([])
        showSnackbar({
          title: t("jobs.editJobsSuccessfully"),
        })
      },
      onError: (error) => {
        if (error.response?.status === 400) {
          showSnackbar({
            title: t("jobs.notAvailablePositionsInJob"),
            variant: "error",
          })
          return
        }
        showSnackbar({
          title: t(getErrorMessageFromTab(error)),
          variant: "error",
        })
      },
    },
  })

  const { mutate: removeStudentProfile } = useMutationRemoveStudent({
    options: {
      onSuccess: () => {
        showSnackbar({
          title: t("studentProfile.studentProfileIsWasDeleted"),
        })
        navigate(RoutePath.HOMEPAGE)
      },
      onError: (error) => {
        showSnackbar({
          title: t(getErrorMessageFromTab(error)),
        })
      },
    },
  })

  const {
    isOpen: isDialogOpenManageStudentClasses,
    handleOpen: handleOpenDialogManageStudentClasses,
    handleClose: handleCloseDialogManageStudentClasses,
  } = useDialog()

  const {
    isOpen: isDialogOpenManageStudentJobs,
    handleOpen: handleOpenDialogManageStudentJobs,
    handleClose: handleCloseDialogManageStudentJobs,
  } = useDialog()

  const {
    isOpen: isDialogOpenDeleteStudentProfile,
    handleOpen: handleOpenDialogDeleteStudentProfile,
    handleClose: handleCloseDialogDeleteStudentProfile,
  } = useDialog()

  const {
    isOpen: isDialogOpenUpgradeAccount,
    handleOpen: handleOpenDialogUpgradeAccount,
    handleClose: handleCloseDialogUpgradeAccount,
  } = useDialog()

  const [tab, setTab] = useState("personal_information")
  const { popoverAnchorEl, handleClick, handleClose } =
    usePopover<HTMLButtonElement>()

  const {
    handleClick: filterClick,
    handleClose: filterClose,
    popoverAnchorEl: filterPopover,
  } = usePopover<HTMLButtonElement>()

  const {
    popoverAnchorEl: popoverAnchorElExport,
    handleClick: handleClickExport,
    handleClose: handleCloseExport,
  } = usePopover<HTMLButtonElement>()

  useEffect(() => {
    if (!popoverAnchorElExport) {
      setIsExportMenuOpenTransactions(false)
      setIsExportMenuOpenJobs(false)
      setIsExportMenuOpenStoreTransactions(false)
    }
  }, [popoverAnchorElExport])

  const handleChange = (_event: SyntheticEvent, newVal: string) => {
    setTab(newVal)
  }

  return (
    <Stack
      width="100%"
      height="100%"
      display="flex"
      alignItems="center"
      justifyContent="flex-start"
    >
      <Grid
        container
        flexDirection="column"
        justifyContent="stretch"
        alignItems="start"
        sx={{
          maxWidth: "920px",
          backgroundColor: theme.palette.mockup.neutral100,
          px: { mobile: 1, tablet: 3 },
        }}
      >
        <Grid display="flex" alignItems="center" sx={{ mt: 4, pl: 0 }}>
          <Button
            sx={{ pl: 0 }}
            onMouseEnter={() => setHover(true)}
            onMouseLeave={() => setHover(false)}
            onClick={() => {
              navigate(
                classId
                  ? RoutePath.CLASS.replace(":classId", `${classId}`)
                  : RoutePath.HOMEPAGE,
              )
            }}
            variant="text"
          >
            <ArrowBack
              style={{
                color: theme.palette.mockup.navy,
                transition: ".3s",
                opacity: hover ? "0.5" : "1",
                marginRight: "16px",
              }}
            />
            {classId ? t("jobs.backToClass") : t("jobs.backToDashboard")}
          </Button>
        </Grid>
        {errorInfo && (
          <Typography variant="h3" color="mockup.error50">
            {errorInfo}
          </Typography>
        )}
        <Grid
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          width="100%"
          sx={{ pl: 0, pr: 2, pt: 1, pb: 3 }}
        >
          {isLoading ? (
            <StudentSkeleton height="50px" />
          ) : (
            <Typography variant="h3" color="mockup.neutral10">
              {isSuccess && data?.data.first_name}{" "}
              {isSuccess && data?.data.last_name}
            </Typography>
          )}
          <Box display="flex" columnGap="10px">
            {tab === "transactions" && (
              <Fab sx={{ flexShrink: 0 }} onClick={filterClick}>
                <IconWrapper height="32px" width="32px" color="primary.main">
                  <IconFilters />
                </IconWrapper>
              </Fab>
            )}
            <Fab
              onClick={(event) => {
                handleClick(event)
                mixpanel.track("student_profile_options")
              }}
              disabled={isLoading}
            >
              {isFetchingExportJobs || isFetchingExportTransactions || isFetchingExportStoreTransactions ? (
                <CircularProgress />
              ) : (
                <ThreeDots style={{ margin: "auto" }} />
              )}
            </Fab>

            <PopOverStyle
              open={!!popoverAnchorEl}
              onClose={handleClose}
              anchorEl={popoverAnchorEl}
              marginThreshold={40}
              anchorOrigin={{
                vertical: 60,
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
            >
              <ul style={{ listStyleType: "none" }}>
                <li>
                  <PopRow
                    sx={{ width: "100%" }}
                    onClick={() => {
                      handleOpenDialogManageStudentClasses()
                      mixpanel.track("student_classes_management")
                    }}
                  >
                    {t("studentProfile.manageStudentClasses")}
                  </PopRow>
                </li>
                <Dialog
                  titleText={t("studentProfile.manageStudentClasses")}
                  open={isDialogOpenManageStudentClasses}
                  actionAcceptButtonProps={{
                    disabled: isLoadingUpdateStudentClass,
                  }}
                  onClose={() => {
                    setChecked([])
                    handleCloseDialogManageStudentClasses()
                    mixpanel.track("student_classes_management", {
                      action: "cancel",
                    })
                  }}
                  actionAcceptText={t("studentProfile.save")}
                  desktopMaxWidth="534px"
                  tabletMaxWidth="534px"
                  onActionButtonClick={() => {
                    const checkedArr: number[] = []

                    if (
                      checked.length === checkedArr.length &&
                      checked.every((el) => checkedArr.includes(el))
                    ) {
                      showSnackbar({
                        title: t("studentProfile.nothingChange"),
                        variant: "info",
                      })
                      return
                    }

                    classes.forEach((el) => {
                      if (el.is_in_class) {
                        return checkedArr.push(el.id)
                      }
                    })

                    mutate({
                      studentId: Number(studentId),
                      classes: checked,
                    })
                  }}
                >
                  <ManageStudentClasses
                    classes={classes}
                    setChecked={setChecked}
                    checked={checked}
                  />
                </Dialog>
                <li>
                  <PopRow onClick={handleOpenDialogManageStudentJobs}>
                    {t("studentProfile.manageStudentJobs")}
                  </PopRow>
                </li>
                <Dialog
                  titleText={t("studentProfile.manageStudentJobs")}
                  open={isDialogOpenManageStudentJobs}
                  onClose={handleCloseDialogManageStudentJobs}
                  actionAcceptText={t("studentProfile.save")}
                  desktopMaxWidth="534px"
                  tabletMaxWidth="534px"
                  onActionButtonClick={() => {
                    editStudentJobs({
                      studentId: Number(studentId),
                      classId: Number(selectedClass),
                      data: checkedJobs,
                    })
                  }}
                >
                  <ManageStudentJobs
                    selectedClass={selectedClass}
                    setSelectedClass={setSelectedClass}
                    studentId={studentId as string}
                    checkedJobs={checkedJobs}
                    setCheckedJobs={setCheckedJobs}
                  />
                </Dialog>
                {hiddenExport(userInfo) && (
                  <li>
                    <PopRow
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        width: "100%",
                      }}
                      onClick={(
                        e: MouseEvent<HTMLButtonElement>
                      ) => {
                        mixpanel.track("reports_export", {
                          method: "student_profile",
                          action: "click",
                        })
                        userInfo?.user.school_plan === UserSchoolPlan.FREE ||
                        userInfo?.user.school_plan === UserSchoolPlan.DEMO
                          ? handleOpenDialogUpgradeAccount()
                          : handleClickExport(e)
                      }}
                    >
                      {t("studentProfile.exportData")}
                      <IconChevronDown
                        style={{
                          width: "16px",
                          height: "16px",
                          transform: "rotate(-90deg)",
                        }}
                      />
                    </PopRow>
                  </li>
                )}
                <DialogUpgradeAccount
                  open={isDialogOpenUpgradeAccount}
                  onClose={() => {
                    handleCloseDialogUpgradeAccount()
                    mixpanel.track("reports_export", {
                      method: "student_profile",
                      action: "cancel",
                    })
                  }}
                  onActionButtonClick={() => {
                    payIt(null)
                    mixpanel.track("reports_export", {
                      method: "student_profile",
                      action: "submit",
                    })
                  }}
                  isLoading={isLoadingPayments}
                  isExport
                />
                <PopOverStyle
                  open={!!popoverAnchorElExport}
                  onClose={handleCloseExport}
                  anchorEl={popoverAnchorElExport}
                  anchorOrigin={{
                    vertical: "top",
                    horizontal: "left",
                  }}
                  transformOrigin={{
                    vertical: "top",
                    horizontal: "right",
                  }}
                >
                  <ul style={{ width: "250px" }}>
                    <li>
                      <PopRow
                        sx={[
                          { width: "100%" },
                          isFetchingExportTransactions
                            ? { opacity: 0.3 }
                            : { opacity: 1 },
                        ]}
                        onClick={handleToggleExportMenuTransactions}
                      >
                        <PopoverBox>
                          <IconWrapper>
                            <IconExport height="100%" width="100%" />
                          </IconWrapper>
                          <Typography
                            component="span"
                            variant="body2"
                            fontWeight="500"
                          >
                            {t("studentProfile.studentTransactions")}
                          </Typography>
                          <IconChevronDown
                            style={{
                              width: "16px",
                              height: "16px",
                              marginLeft: "auto",
                              transform: isExportMenuOpenTransactions
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.2s ease",
                              color: theme.palette.text.secondary,
                            }}
                          />
                        </PopoverBox>
                      </PopRow>
                    </li>
                    <InlineExportSubmenu
                      open={isExportMenuOpenTransactions}
                      successTasks={successTasksTransactions}
                      onGenerateNewFile={() => {
                        handleToggleExportMenuTransactions()
                        schoolPlanToExportData({
                          user: userInfo,
                          upgrade: handleOpenDialogUpgradeAccount,
                          exportData: () => {
                            setIsExportDataTransactions(true)
                            refetchExportDataTransactions()
                          },
                        })
                      }}
                      onFileClick={setExportReportIdToDownloadTransactions}
                      isGenerateDisabled={isDisabledTransactions}
                      generateDisabledTooltip={t("export.reportCurrentlyBeingGenerated")}
                    />

                    <li>
                      <PopRow
                        sx={[
                          { width: "100%" },
                          isFetchingExportJobs
                            ? { opacity: 0.3 }
                            : { opacity: 1 },
                        ]}
                        onClick={handleToggleExportMenuJobs}
                      >
                        <PopoverBox>
                          <IconWrapper>
                            <IconExport height="100%" width="100%" />
                          </IconWrapper>
                          <Typography
                            component="span"
                            variant="body2"
                            fontWeight="500"
                          >
                            {t("studentProfile.studentJobs")}
                          </Typography>
                          <IconChevronDown
                            style={{
                              width: "16px",
                              height: "16px",
                              marginLeft: "auto",
                              transform: isExportMenuOpenJobs
                                ? "rotate(180deg)"
                                : "rotate(0deg)",
                              transition: "transform 0.2s ease",
                              color: theme.palette.text.secondary,
                            }}
                          />
                        </PopoverBox>
                      </PopRow>
                    </li>
                    <InlineExportSubmenu
                      open={isExportMenuOpenJobs}
                      successTasks={successTasksJobs}
                      onGenerateNewFile={() => {
                        handleToggleExportMenuJobs()
                        schoolPlanToExportData({
                          user: userInfo,
                          upgrade: handleOpenDialogUpgradeAccount,
                          exportData: () => {
                            setIsExportDataJobs(true)
                            refetchExportDataJobs()
                          },
                        })
                      }}
                      onFileClick={setExportReportIdToDownloadJobs}
                      isGenerateDisabled={isDisabledJobs}
                      generateDisabledTooltip={t("export.reportCurrentlyBeingGenerated")}
                    />

                    {state?.name !== "searchBar" && (
                      <li>
                        <PopRow
                          sx={[
                            { width: "100%" },
                            isFetchingExportStoreTransactions
                              ? { opacity: 0.3 }
                              : { opacity: 1 },
                          ]}
                          onClick={handleToggleExportMenuStoreTransactions}
                        >
                          <PopoverBox>
                            <IconWrapper>
                              <IconExport height="100%" width="100%" />
                            </IconWrapper>
                            <Typography
                              component="span"
                              variant="body2"
                              fontWeight="500"
                            >
                              {t("studentProfile.studentStoreTransactions")}
                            </Typography>
                            <IconChevronDown
                              style={{
                                width: "16px",
                                height: "16px",
                                marginLeft: "auto",
                                transform: isExportMenuOpenStoreTransactions
                                  ? "rotate(180deg)"
                                  : "rotate(0deg)",
                                transition: "transform 0.2s ease",
                                color: theme.palette.text.secondary,
                              }}
                            />
                          </PopoverBox>
                        </PopRow>
                      </li>
                    )}
                    {state?.name !== "searchBar" && (
                      <InlineExportSubmenu
                        open={isExportMenuOpenStoreTransactions}
                        successTasks={successTasksStoreTransactions}
                        onGenerateNewFile={() => {
                          if (!exportClassId) {
                            showSnackbar({
                              title: t("studentProfile.selectClassToExport"),
                              variant: "error",
                            })
                            return
                          }
                          schoolPlanToExportData({
                            user: userInfo,
                            upgrade: handleOpenDialogUpgradeAccount,
                            exportData: () => {
                              setIsExportDataStoreTransactions(true)
                              refetchExportDataStoreTransactions()
                              handleToggleExportMenuStoreTransactions()
                            },
                          })
                        }}
                        onFileClick={setExportReportIdToDownloadStoreTransactions}
                        isGenerateDisabled={isDisabledStoreTransactions}
                        generateDisabledTooltip={t("export.reportCurrentlyBeingGenerated")}
                      />
                    )}
                  </ul>
                </PopOverStyle>
                {canDeleteStudent && (
                  <>
                    <li>
                      <PopRow onClick={handleOpenDialogDeleteStudentProfile}>
                        {t("studentProfile.deleteProfile")}
                      </PopRow>
                    </li>
                    <DialogRemoveStudent
                      titleText={t(
                        "studentProfile.areYouSureYouWantToDeleteProfile",
                        {
                          firstName: data?.data.first_name,
                          lastName: data?.data.last_name,
                        }
                      )}
                      isOpen={isDialogOpenDeleteStudentProfile}
                      onClose={handleCloseDialogDeleteStudentProfile}
                      actionAcceptText={t("jobs.delete")}
                      requireDeleteConfirmation
                      descriptionText={t(
                        "studentProfile.thisActionCannotBeUndone"
                      ).toUpperCase()}
                      descriptionTextProps={{
                        variant: "body1",
                        fontWeight: "700",
                        color: "mockup.error50",
                      }}
                      onActionButtonClick={() => {
                        removeStudentProfile({
                          studentId: Number(params.studentId),
                        })
                      }}
                    />
                  </>
                )}
              </ul>
            </PopOverStyle>
          </Box>
        </Grid>
        <Grid width="100%">
          <Box>
            <TabsStyle
              onChange={handleChange}
              value={tab}
              variant="scrollable"
              scrollButtons
              allowScrollButtonsMobile
            >
              <TabStyle
                value="personal_information"
                label={t("studentProfile.personalInformation")}
              />
              <TabStyle
                value="transactions"
                label={t("studentProfile.transactions")}
              />
              <TabStyle value="accounts" label={t("studentProfile.accounts")} />
              <TabStyle value="goals" label={t("studentProfile.goals")} />
            </TabsStyle>
          </Box>
          {isLoading ? (
            <StudentSkeleton height="150px" />
          ) : (
            <>
              {tab === "personal_information" && (
                <>
                  <Grid>
                    <Typography
                      display="block"
                      variant="body3"
                      color="mockup.neutral40"
                      pb="12px"
                      pt="24px"
                    >
                      Picture
                    </Typography>
                  </Grid>
                  <form
                    onSubmit={methods.handleSubmit((data) =>
                      setVal(data.avatar_url)
                    )}
                  >
                    <FormProvider {...methods}>
                      <Grid
                        display="flex"
                        alignItems={mobile ? "flex-start" : "center"}
                        flexDirection={mobile ? "column" : "row"}
                        rowGap={1}
                      >
                        <FileField
                          fileType="image/png, image/jpeg"
                          name="avatar_url"
                          onImageAdd={(e) => setVal(e)}
                          onImageRemove={() => setVal(null)}
                        />
                      </Grid>
                    </FormProvider>
                  </form>
                  <Divider
                    style={{
                      width: "100%",
                      margin: "24px 0",
                      borderColor: theme.palette.mockup.neutral90,
                    }}
                  />
                </>
              )}
            </>
          )}
          {tab === "personal_information" && (
            <AccountInformation
              classId={classId}
              val={val}
              userInfo={data?.data}
              isLoadingRetrieveStudent={isLoading}
              state={state}
            />
          )}
          {tab === "transactions" && (
            <Transactions
              filterClose={filterClose}
              filterPopover={filterPopover}
            />
          )}
          {tab === "accounts" && <Accounts />}
          {tab === "goals" && <Goals />}
        </Grid>
      </Grid>
      <ExportPendingBanner
        visible={
          pendingTasksTransactions.length > 0 ||
          pendingTasksJobs.length > 0 ||
          pendingTasksStoreTransactions.length > 0
        }
      />
    </Stack>
  )
}

export default StudentProfile
